import { useEffect } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { useGetControl } from '../../../../../traits/api/controls/useGetControl';
import { nullsToUndefined } from '../../../../../traits/utils/nullsToUndefined';

export const summarySchema = z.object({
  shellProtectionRisk: z.number().optional(),
  workEnvironmentRisk: z.number().optional(),
  workRegisterRisk: z.number().optional(),
  preApprovedSubcontractorRisk: z.number().optional(),
  workPermitRisk: z.number().optional(),
  id06DeviationsRisk: z.number().optional(),
  identificationRisk: z.number().optional(),
  secondmentRegulationsRisk: z.number().optional(),
  intoxicationRisk: z.number().optional(),
  safetyEquipmentRisk: z.number().optional(),
  otherRisk: z.number().optional(),
});

export type SummaryFormValues = z.infer<typeof summarySchema>;

const riskOrProbability = (
  risk: number | null | undefined,
  probability: number | null | undefined
) => risk ?? probability ?? undefined;

export const useSummaryForm = (inspectionId: string) => {
  const control = useGetControl(inspectionId);

  const methods = useForm<SummaryFormValues>({
    mode: 'onTouched',
    resolver: zodResolver(summarySchema),
  });

  useEffect(() => {
    if (!control.data) return;

    const {
      shellProtectionRisk,
      shellProtectionProbability,
      workEnvironmentRisk,
      workEnvironmentProbability,
      workRegisterRisk,
      workRegisterProbability,
      preApprovedSubcontractorRisk,
      preApprovedSubcontractorProbability,
      workPermitRisk,
      workPermitProbability,
      id06DeviationsRisk,
      id06DeviationsProbability,
      identificationRisk,
      identificationProbability,
      secondmentRegulationsRisk,
      secondmentRegulationsProbability,
      intoxicationRisk,
      intoxicationProbability,
      safetyEquipmentRisk,
      safetyEquipmentProbability,
      otherRisk,
      otherProbability,
    } = control.data;

    methods.reset(
      nullsToUndefined({
        shellProtectionRisk: riskOrProbability(
          shellProtectionRisk,
          shellProtectionProbability
        ),
        workEnvironmentRisk: riskOrProbability(
          workEnvironmentRisk,
          workEnvironmentProbability
        ),
        workRegisterRisk: riskOrProbability(
          workRegisterRisk,
          workRegisterProbability
        ),
        preApprovedSubcontractorRisk: riskOrProbability(
          preApprovedSubcontractorRisk,
          preApprovedSubcontractorProbability
        ),
        workPermitRisk: riskOrProbability(workPermitRisk, workPermitProbability),
        id06DeviationsRisk: riskOrProbability(
          id06DeviationsRisk,
          id06DeviationsProbability
        ),
        identificationRisk: riskOrProbability(
          identificationRisk,
          identificationProbability
        ),
        secondmentRegulationsRisk: riskOrProbability(
          secondmentRegulationsRisk,
          secondmentRegulationsProbability
        ),
        intoxicationRisk: riskOrProbability(
          intoxicationRisk,
          intoxicationProbability
        ),
        safetyEquipmentRisk: riskOrProbability(
          safetyEquipmentRisk,
          safetyEquipmentProbability
        ),
        otherRisk: riskOrProbability(otherRisk, otherProbability),
      })
    );
  }, [control.isSuccess]);

  return methods;
};
